// S8.1 — useLastVerdict.
//
// Fetches /api/v2/audit/last-council/:symbol and exposes the last Council
// verdict for the symbol so callers (ReportsPage, Home, dashboards) can
// gate Execute / Send buttons on a single source of truth.
//
// The hook returns enough context to render a Verification Checklist card:
//   verdict       — "approved" | "needs_revision" | "blocked" | undefined
//   decisionId    — id from the last council record
//   reason        — short string for display when blocked
//   loading/error — UX states
//
// Symbol changes trigger a re-fetch; when symbol is empty the hook is
// quiescent (no requests, no state churn).

import { useEffect, useState } from 'react';
import { getApiUrl } from '../utils/apiConfig';
import { authHeaders, getRole } from '../utils/role';
import { useAuth } from '../context/AuthContext';

/** Server entitlement for every ARIN council / decision-audit read (owner 2026-10-08): admin-only today, the future
 *  Institutional feature. The UI hides council surfaces for callers without it (the server answers 402). */
export const COUNCIL_AUDIT_FEATURE = 'council_audit';

/** True when the caller may read council / decision-audit data (admin role, or the council_audit entitlement). */
export function useCouncilAuditAccess(): boolean {
  const { has, role } = useAuth();
  return role === 'admin' || getRole() === 'admin' || has(COUNCIL_AUDIT_FEATURE);
}

export type CouncilVerdict =
  | 'approved'
  | 'needs_revision'
  | 'draft'
  | 'blocked'
  | 'unknown';

export interface LastVerdict {
  verdict: CouncilVerdict | undefined;
  decisionId: string | undefined;
  reason: string | undefined;
  symbol: string;
  loading: boolean;
  error: string | undefined;
  /** false when the caller lacks council_audit: no request is made and callers render nothing. */
  allowed: boolean;
  refetch: () => void;
}

interface CouncilResponse {
  success?: boolean;
  decision_id?: string;
  decision?: Record<string, unknown> | null;
  error?: string;
}

function readReason(decision: Record<string, unknown> | null | undefined): string | undefined {
  if (!decision) return undefined;
  for (const key of ['reason', 'rationale', 'summary', 'message']) {
    const v = decision[key];
    if (typeof v === 'string' && v.trim().length > 0) return v;
  }
  return undefined;
}

export function useLastVerdict(symbol: string | null | undefined): LastVerdict {
  const [verdict, setVerdict] = useState<CouncilVerdict | undefined>(undefined);
  const [decisionId, setDecisionId] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [tick, setTick] = useState(0);

  const allowed = useCouncilAuditAccess();
  const sym = allowed ? (symbol || '').trim().toUpperCase() : '';

  useEffect(() => {
    if (!sym) {
      setVerdict(undefined);
      setDecisionId(undefined);
      setReason(undefined);
      setError(undefined);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    fetch(getApiUrl(`/api/v2/audit/last-council/${encodeURIComponent(sym)}`), {
      headers: authHeaders(),
    })
      .then(async res => {
        const json = (await res.json()) as CouncilResponse;
        if (cancelled) return;
        if (!res.ok || json.success === false) {
          setError(json.error || `HTTP ${res.status}`);
          setVerdict(undefined);
          setDecisionId(undefined);
          setReason(undefined);
          return;
        }
        const dec = json.decision || null;
        const v = (dec && typeof dec.verdict === 'string'
          ? (dec.verdict as string)
          : '') as CouncilVerdict | '';
        setVerdict(v ? (v as CouncilVerdict) : undefined);
        setDecisionId(json.decision_id);
        setReason(readReason(dec));
      })
      .catch(e => {
        if (!cancelled) setError(e?.message || 'fetch failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sym, tick]);

  return {
    verdict,
    decisionId,
    reason,
    symbol: sym,
    loading,
    error,
    allowed,
    refetch: () => setTick(t => t + 1),
  };
}

// Helper for buttons: returns true when the supplied verdict should block
// any Execute / Send / Download action.
export function isBlocking(v: CouncilVerdict | undefined): boolean {
  return v === 'blocked';
}
